import React from 'react';
import { useForm } from 'react-hook-form';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import { allowedUrlsValidationConfig } from '../../../../types/ValidationTypes';
import { parseUrlLines } from '../../../../services/helpers/urlList';
import Spinner from '../../../atoms/Spinner/Spinner';
import ErrorMessage from '../../../atoms/Message/Error/ErrorMessage';
import ModalContainer from '../../../atoms/Modal/ModalContainer';
import HeaderTitle from '../../../atoms/Modal/HeaderTitle';
import FormRow from '../../../atoms/Form/FormRow';
import GridTextArea from '../../../atoms/Input/GridTextArea';
import FooterButtonContainer from '../../../atoms/Modal/FooterButtonContainer';
import Button from '../../../atoms/Button/Button';
import Flex from '../../../atoms/Box/Flex';
import Text from '../../../atoms/Text/Text';
import { ErrorType } from '../../../../types/response/ErrorCodes';
import {
    getAllowedUrls,
    getMerchantKey,
    hasMerchantChanges,
    hasMerchantName,
    Merchant,
    MerchantChanges,
} from '../../../../types/onboarding/Merchant';
import { useOnboardingStore } from '../../../../store/admin/onboarding/onboardingStore';
import { formatOrganizationNumber } from '../../../../services/helpers/organizationNumber';

interface EditMerchantFormProps {
    merchant: Merchant;
    /** Called with the fields that differ from the merchant; not called when nothing was changed. */
    onSave: (changes: MerchantChanges) => void;
    hide: () => void;
    theme: DefaultTheme;
}

/** The form fields, one per editable field of the merchant, in the shape the inputs hold them. */
type Inputs = {
    /** One https:// URL per line; parsed into the allowed URLs of the merchant on submit. */
    allowedUrls: string;
};

export const storeUrlsHint =
    'One https:// address per line, without a path, such as https://shop.example.se. A store can only be onboarded on one of them. Stores already onboarded keep their address.';

/** The form values for the merchant as it is, so that only what the user changes ends up in the patch. */
const toInputs = (merchant: Merchant): Inputs => ({
    allowedUrls: getAllowedUrls(merchant).join('\n'),
});

const sameList = (a: string[], b: string[]) =>
    a.length === b.length && a.every((value, index) => value === b[index]);

/** The fields of the merchant the submitted values differ from, each in the shape the API takes. */
const toChanges = (merchant: Merchant, data: Inputs): MerchantChanges => {
    const changes: MerchantChanges = {};
    const allowedUrls = parseUrlLines(data.allowedUrls);

    if (!sameList(allowedUrls, getAllowedUrls(merchant))) changes.allowedUrls = allowedUrls;

    return changes;
};

/**
 * Edits the fields of a merchant that can be changed after it was added (MerchantChanges), one section per field,
 * prefilled with the current values. Only the fields the user changed are saved; saving nothing just closes the form.
 * Errors are kept in the onboarding store, so the entered values stay in place for a retry; the modal is closed by the
 * caller once the merchant is saved.
 */
const EditMerchantForm: React.FC<EditMerchantFormProps> = ({
    merchant,
    onSave,
    hide,
    theme,
}: EditMerchantFormProps) => {
    const { savingMerchantKey, saveMerchantError } = useOnboardingStore();

    const isSaving = savingMerchantKey === getMerchantKey(merchant);

    const form = useForm<Inputs>({
        mode: 'onSubmit',
        shouldUseNativeValidation: true,
        defaultValues: toInputs(merchant),
    });
    const { getValues, register, handleSubmit } = form;

    const allowedUrls = register('allowedUrls', allowedUrlsValidationConfig);

    const onSubmit = (data: Inputs) => {
        const changes = toChanges(merchant, data);

        if (hasMerchantChanges(changes)) onSave(changes);
        else hide();
    };

    const merchantName = hasMerchantName(merchant)
        ? merchant.name
        : formatOrganizationNumber(merchant.countryCode, merchant.organizationNumber);

    return (
        <>
            <ModalContainer position="header">
                <HeaderTitle>Edit merchant</HeaderTitle>
                <Text secondary data-testid="edit-merchant-name">
                    {merchantName}
                </Text>
            </ModalContainer>

            <ModalContainer position="content" noScroll={false}>
                <Content
                    as="form"
                    id="edit-merchant-form"
                    data-testid="form"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Section data-testid="edit-merchant-urls">
                        <SectionHeader>
                            <Text medium>Store URLs</Text>
                            <Text secondary small>
                                The addresses a store of the merchant can be onboarded on.
                            </Text>
                        </SectionHeader>
                        <FormRow first last>
                            <GridTextArea
                                id="allowedUrls"
                                placeholder="Allowed store URLs"
                                placeholderRaised={!!getValues(allowedUrls.name)}
                                setRef={allowedUrls.ref}
                                name={allowedUrls.name}
                                onChange={allowedUrls.onChange}
                                disabled={isSaving}
                                rows={5}
                                dataTestId="merchant-urls"
                            />
                        </FormRow>
                        <Text secondary small>
                            {storeUrlsHint}
                        </Text>
                    </Section>
                </Content>
            </ModalContainer>

            {saveMerchantError && (
                <ModalContainer position="error">
                    <ErrorMessage error={saveMerchantError} errorHeader={ErrorType.SaveMerchant} />
                </ModalContainer>
            )}

            <ModalContainer position="footer">
                <FooterButtonContainer>
                    <Button
                        onClick={hide}
                        large
                        disabled={isSaving}
                        data-testid="cancel-edit-merchant"
                    >
                        Cancel
                    </Button>
                    <Button
                        tabIndex={0}
                        form="edit-merchant-form"
                        type="submit"
                        large
                        primary
                        disabled={isSaving}
                        data-testid="save-merchant"
                    >
                        {isSaving ? (
                            <Spinner color={theme.colors.light} size={8} loading />
                        ) : (
                            <span>Save</span>
                        )}
                    </Button>
                </FooterButtonContainer>
            </ModalContainer>
        </>
    );
};

/** The sections of the form, one per editable field, stacked. */
const Content = styled(Flex).attrs(props => ({
    column: true,
    gap: props.theme.layout.gap.medium,
}))``;

/** The header of a field, its input and the hint below it, stacked. */
const Section = styled(Flex).attrs(props => ({
    column: true,
    gap: props.theme.layout.gap.xsmall,
}))``;

/** The title of a field and what it holds, side by side. */
const SectionHeader = styled(Flex).attrs(props => ({
    alignItems: 'baseline',
    gap: props.theme.layout.gap.small,
}))``;

export default withTheme(EditMerchantForm) as React.ComponentType<
    Omit<EditMerchantFormProps, 'theme'>
>;
